<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';

import Logo from '@/assets/Apexx_logo_square.png';
import AppUpdateDrawer from '@/components/AppUpdateDrawer.vue';
import { useAppUpdateCheck } from '@/composables/useAppUpdateCheck';
import { useHubSettingsDrawer } from '@/composables/useHubSettingsDrawer';
import { useLauncherStatus } from '@/composables/useLauncherStatus';
import { getAccessibleApps } from '@/shell/orchestrator';
import { consumeWelcomeSidebarAutoOpen } from '@/shell/welcomeSidebarAutoOpen';
import { useHubAuthStore } from '@/store/hubAuthStore';
import { decodeJwtPayload } from '@apexx/auth';
import {
	AppShellMobileTopBar,
	AppVersionBadge,
	DualPaneSidebar,
	SharedAppShell,
	buildHubRailApps,
} from '@apexx/app-shell';
import {
	Button,
	Icon,
	OverviewActivity,
	Typography,
	type OverviewMetric,
} from '@apexx/component-library';
import { canSwitchHubApps } from '@apexx/shell-contract';

type JwtUserPayload = {
	name?: string;
	preferred_username?: string;
	email?: string;
};

definePage({
	name: 'launcher',
	meta: { requiresAuth: true },
});

const router = useRouter();
const authStore = useHubAuthStore();
const shellRef = ref<{
	openMobileSidebar: () => void;
	closeMobileSidebar: () => void;
} | null>(null);
const { openSettingsDrawer } = useHubSettingsDrawer();

const accessibleApps = computed(() => getAccessibleApps(authStore.accessToken));
const showAppSwitcher = computed(() => canSwitchHubApps(authStore.accessToken));
const railApps = computed(() => buildHubRailApps('', authStore.accessToken));

const {
	drawerOpen: updateDrawerOpen,
	currentDisplayVersion,
	availableDisplayVersion,
	openAppStore,
	dismissUpdate,
} = useAppUpdateCheck();

const signedInAs = computed(() => {
	const token = authStore.accessToken;
	if (!token) {
		return null;
	}

	const payload = decodeJwtPayload(token) as JwtUserPayload | undefined;

	return (
		payload?.name?.trim() ||
		payload?.preferred_username?.trim() ||
		payload?.email?.trim() ||
		null
	);
});

const { widgets } = useLauncherStatus(accessibleApps);

const metrics = computed((): OverviewMetric[] =>
	widgets.value.map(({ loading: _loading, ...metric }) => metric),
);

function openApp(appId: string): void {
	void router.push({ name: 'app-shell', params: { appId } });
}

function openHubHome(): void {
	void router.push({ name: 'launcher' });
}

function onMetricSelect(metric: OverviewMetric): void {
	if (metric.appId) {
		openApp(metric.appId);
	}
}

function onOpenActivity(): void {
	const first = widgets.value.find((w) => w.appId);
	if (first?.appId) {
		openApp(first.appId);
	}
}

function openSettings(): void {
	shellRef.value?.closeMobileSidebar();
	openSettingsDrawer();
}

onMounted(async () => {
	if (!showAppSwitcher.value || !consumeWelcomeSidebarAutoOpen()) {
		return;
	}

	await nextTick();
	shellRef.value?.openMobileSidebar();
});
</script>

<template>
	<SharedAppShell
		ref="shellRef"
		:is-embedded="false"
		:show-app-switcher="showAppSwitcher"
		@open-settings="openSettings"
	>
		<template #sidebar-title>
			<Typography variant="body" class="text-white">
				Overview
			</Typography>
		</template>

		<template
			#desktop-sidebar="{
				showAppSwitcher: canSwitchApps,
				openSettings: onOpenSettings,
			}"
		>
			<DualPaneSidebar
				active-app-id=""
				title=""
				:rail-apps="railApps"
				:items="[]"
				:access-token="authStore.accessToken"
				:show-app-switcher="canSwitchApps"
				@open-settings="onOpenSettings"
				@open-app="openApp"
				@open-hub-home="openHubHome"
			>
				<template #panel>
					<div class="flex flex-1 items-center justify-center px-4">
						<p class="text-center text-sm text-gray-400">
							Select an app
						</p>
					</div>
				</template>
			</DualPaneSidebar>
		</template>

		<template
			#mobile-sidebar="{
				close,
				showAppSwitcher: canSwitchApps,
				openSettings: onOpenSettings,
			}"
		>
			<DualPaneSidebar
				active-app-id=""
				title=""
				:rail-apps="railApps"
				:items="[]"
				:access-token="authStore.accessToken"
				:show-close="true"
				:show-app-switcher="canSwitchApps"
				@close="close"
				@open-settings="onOpenSettings"
				@open-app="openApp"
				@open-hub-home="openHubHome"
			>
				<template #panel>
					<div class="flex flex-1 items-center justify-center px-4">
						<p class="text-center text-sm text-gray-400">
							Select an app
						</p>
					</div>
				</template>
			</DualPaneSidebar>
		</template>

		<template #default="{ openMobileSidebar }">
			<div
				class="flex min-h-0 min-w-0 w-full flex-1 flex-col bg-primary/5"
			>
				<main
					class="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
				>
					<div class="lg:hidden">
						<AppShellMobileTopBar>
							<template #leading>
								<button
									v-if="showAppSwitcher"
									type="button"
									aria-label="Open menu"
									@click="openMobileSidebar"
								>
									<Icon
										name="menu"
										size="md"
										class="text-white"
									/>
								</button>
							</template>
							<template #center>
								<Typography variant="body" class="text-white">
									Overview
								</Typography>
							</template>
						</AppShellMobileTopBar>
					</div>

					<div
						class="mx-auto flex w-full max-w-lg flex-1 flex-col overflow-y-auto px-6"
						style="
							padding-top: max(1.5rem, var(--safe-area-top));
							padding-bottom: var(--safe-area-bottom);
						"
					>
						<div class="relative pt-2">
							<div
								class="absolute right-0 top-2 z-10 max-lg:hidden"
							>
								<AppVersionBadge />
							</div>
						</div>

						<div class="flex-1 pb-6 pt-2">
							<Typography
								v-if="accessibleApps.length === 0"
								variant="body"
								class="rounded-2xl bg-white p-8 text-center text-on-surface-muted"
								as="p"
							>
								No applications are available for your account.
							</Typography>

							<div
								v-else-if="widgets.length === 0"
								class="rounded-2xl bg-white p-8 text-center"
							>
								<Typography
									variant="body"
									class="text-on-surface-muted"
									as="p"
								>
									No status widgets for your apps yet.
								</Typography>
							</div>

							<OverviewActivity
								v-else
								:logo-src="Logo"
								title="Welcome"
								subtitle="Your activity at a glance"
								:metrics="metrics"
								@select="onMetricSelect"
								@action="onOpenActivity"
							/>
						</div>
					</div>
				</main>
			</div>
		</template>
	</SharedAppShell>

	<AppUpdateDrawer
		v-model:open="updateDrawerOpen"
		:current-display-version="currentDisplayVersion"
		:available-display-version="availableDisplayVersion"
		@update="openAppStore"
		@dismiss="dismissUpdate"
	/>
</template>
